import { exportProductsMinimumStockToXlsx, uploadProductsMinimumStockExcel } from '@gozoki/api'
import type { RowError } from '@gozoki/api-types'
import { AxiosError } from 'axios'
import { Download, Loader2, Upload } from 'lucide-react'
import { useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Button } from '~/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog'
import { downloadBlob } from '~/utils/excel/downloadBlob'

interface ReapproStockMinBulkDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface ImportResult {
  created: number
  updated: number
  failed: number
  errors: RowError[]
}

export const ReapproStockMinBulkDialog = ({
  open,
  onOpenChange,
}: ReapproStockMinBulkDialogProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [importResult, setImportResult] = useState<ImportResult | null>(null)
  const [importError, setImportError] = useState<string | null>(null)

  const { mutate: exportXlsx, isPending: isExporting } = useMutation({
    mutationFn: exportProductsMinimumStockToXlsx,
  })
  const { mutate: importXlsx, isPending: isImporting } = useMutation({
    mutationFn: uploadProductsMinimumStockExcel,
  })

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen)
    if (!nextOpen) {
      setSelectedFile(null)
      setImportResult(null)
      setImportError(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleExport = () => {
    exportXlsx(undefined, {
      onSuccess: (response) => {
        downloadBlob({ blob: response.data, filename: 'export-stock-min-fixe-reappro.xlsx' })
      },
      onError: (error) => {
        const message =
          error instanceof AxiosError
            ? (error.response?.data?.message ?? "Erreur lors de l'export")
            : "Erreur lors de l'export"
        toast.error(message)
      },
    })
  }

  const handleImport = () => {
    if (!selectedFile) return

    setImportResult(null)
    setImportError(null)

    importXlsx(selectedFile, {
      onSuccess: (data) => {
        setImportResult(data)
        setSelectedFile(null)
        if (fileInputRef.current) fileInputRef.current.value = ''
      },
      onError: (error) => {
        if (error instanceof AxiosError && error.response?.data) {
          const data = error.response.data
          setImportError(data.message ?? "Erreur lors de l'import")
        } else {
          setImportError("Une erreur inconnue est survenue lors de l'import")
        }
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Stock min en masse</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Exportez le fichier pour consulter les stocks min de tous les produits en régime stock
            minimum, puis réimportez-le après modification.
          </p>

          {/* Export */}
          <Button
            variant="outline"
            className="w-full"
            onClick={handleExport}
            disabled={isExporting}
          >
            {isExporting ? <Loader2 className="animate-spin" /> : <Download />}
            Exporter le fichier XLSX
          </Button>

          <div className="border-t border-border" />

          {/* Import */}
          <div className="space-y-2">
            <label className="text-xs text-muted-foreground uppercase tracking-wider">
              Réimporter un fichier modifié
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              onChange={(e) => {
                setSelectedFile(e.target.files?.[0] ?? null)
                setImportResult(null)
                setImportError(null)
              }}
              className="w-full text-sm file:mr-3 file:rounded-md file:border file:border-border file:bg-muted file:px-3 file:py-1.5 file:text-sm file:font-medium hover:file:bg-muted/80"
            />
          </div>

          {/* Import error */}
          {importError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {importError}
            </div>
          )}

          {/* Import result */}
          {importResult && (
            <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 space-y-1">
              <p className="text-sm font-medium text-green-800">Import terminé</p>
              <ul className="text-sm text-green-700 list-disc list-inside">
                <li>{importResult.created} créé(s)</li>
                <li>{importResult.updated} mis à jour</li>
                {importResult.failed > 0 && (
                  <li className="text-red-600">{importResult.failed} erreur(s)</li>
                )}
              </ul>
              {importResult.errors.length > 0 && (
                <ul className="mt-2 text-xs text-red-600 space-y-0.5">
                  {importResult.errors.map((err, i) => (
                    <li key={i}>
                      {err.reference} : {err.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>Fermer</DialogClose>
          <Button onClick={handleImport} disabled={!selectedFile || isImporting}>
            {isImporting ? <Loader2 className="animate-spin" /> : <Upload />}
            Importer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
